import React__default, { forwardRef, useRef, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';
import { useStepsContext } from './StepsContext.js';
import Box from '../Box/Box.js';
import { StepIcon } from './StepIcon.js';
import { StepLabel } from './StepLabel.js';
import { Connector } from './Connector.js';
import Collapse from '../Collapse/Collapse.js';

function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
const Step = /*#__PURE__*/forwardRef(({
  ...props
}, forwardedRef) => {
  const innerRef = useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, innerRef);
  const {
    children,
    label,
    description,
    icon,
    state,
    checkIcon,
    index,
    isCompletedStep,
    isCurrentStep,
    isLastStep,
    isKeepError,
    size = 'md',
    uncollapsed = false,
    ...styleProps
  } = props;
  const {
    isVertical,
    isError,
    isLoading,
    isLabelVertical,
    checkIcon: defaultCheckIcon,
    onSelectStep,
    clickable,
    setWidths,
    stepCount
  } = useStepsContext();
  const stepContainerStyles = {
    display: 'flex',
    alignItems: 'center'
  };
  const stepIconContainerStyles = {
    display: 'flex',
    borderRadius: '50%',
    alignItems: 'center',
    justifyContent: 'center',
    bg: 'background.neutral.default.[default]',
    borderColor: 'border.default',
    transitionProperty: 'background, border-color',
    transitionDuration: 'normal',
    _activeStep: {
      bg: 'background.neutral.default.[default]',
      borderColor: 'text.default',
      _invalid: {
        bg: 'background.danger.bold.default',
        borderColor: 'border.danger'
      }
    },
    _highlighted: {
      bg: 'background.success.default.[default]',
      borderColor: 'border.success'
    },
    '&[data-clickable]:hover': {
      borderColor: 'background.success.default.hovered'
    },
    sm: {
      width: '32px',
      height: '32px',
      borderWidth: '2px'
    },
    md: {
      width: '40px',
      height: '40px',
      borderWidth: '2px'
    },
    lg: {
      width: '48px',
      height: '48px',
      borderWidth: '2px'
    }
  };
  const hasVisited = isCurrentStep || isCompletedStep;
  const opacity = hasVisited ? 1 : 0.8;
  const handleClick = index => {
    if (clickable && onSelectStep) {
      onSelectStep(index);
    }
  };
  const containerRef = useCallback(node => {
    if (node && setWidths) {
      setWidths(prev => {
        if (prev.length === stepCount) {
          return [node.offsetWidth || 0];
        }
        return [...prev, node.offsetWidth || 0];
      });
    }
  },
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  [stepIconContainerStyles[size].height, stepIconContainerStyles[size].width]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(Box, _extends({
    as: "li",
    ref: innerRef,
    onClick: () => handleClick(index),
    "aria-disabled": !hasVisited,
    sx: {
      opacity,
      flexDirection: isVertical ? 'column' : 'row',
      alignItems: isVertical || isLabelVertical ? 'flex-start' : 'center',
      flex: isLastStep && !isVertical ? '0 0 auto' : '1 0 auto',
      justifyContent: isLastStep && !isVertical ? 'flex-end' : 'flex-start',
      '&:hover': {
        cursor: clickable ? 'pointer' : 'default'
      },
      display: 'flex',
      position: 'relative'
    }
  }, styleProps), /*#__PURE__*/React__default.createElement(Box, {
    as: "div",
    ref: containerRef,
    sx: {
      flexDirection: isLabelVertical ? 'column' : 'row',
      ...stepContainerStyles
    }
  }, /*#__PURE__*/React__default.createElement(Box, {
    as: "div",
    sx: {
      display: 'flex',
      borderRadius: '50%',
      alignItems: 'center',
      justifyContent: 'center',
      borderStyle: 'solid',
      borderWidth: '1px',
      color: isCurrentStep && isError ? 'text.inverse' : isCurrentStep && isKeepError ? 'text.inverse' : isCompletedStep ? 'text.inverse' : 'revert',
      bg: isCompletedStep && !isKeepError ? 'background.success.bold.default' : isCurrentStep && isError ? 'background.danger.bold.default' : isCurrentStep && isKeepError ? 'background.danger.bold.default' : isCurrentStep && isKeepError ? 'background.danger.bold.default' : isCompletedStep && isKeepError ? 'backround.danger.bold.default' : 'background.neutral.default.[default]',
      borderColor: isCurrentStep && isError ? 'border.danger' : isCurrentStep && isKeepError ? 'border.danger' : isCurrentStep ? 'border.success' : isCompletedStep && !isKeepError ? 'border.success' : isCompletedStep && !isKeepError ? 'border.sucess' : 'border.default',
      transitionProperty: 'background, border-color',
      transitionDuration: 'normal',
      _activeStep: {
        bg: 'background.neutral.default.[default]',
        borderColor: 'text.default',
        _invalid: {
          bg: 'background.danger.default.[default]',
          borderColor: 'border.danger'
        }
      },
      _highlighted: {
        bg: 'background.sucess.default',
        borderColor: 'border.success'
      },
      '&[data-clickable]:hover': {
        borderColor: 'border.sucess'
      },
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      width: size === 'sm' ? '32px' : size === 'md' ? '40px' : size === 'lg' ? '48px' : '32px',
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      height: size === 'sm' ? '32px' : size === 'md' ? '40px' : size === 'lg' ? '48px' : '32px'
    },
    "aria-current": hasVisited && isKeepError || isCurrentStep ? 'step' : undefined,
    "data-invalid": Boolean((hasVisited && isKeepError || isCurrentStep) && (isError || state === 'error')),
    "data-highlighted": Boolean(isCompletedStep),
    "data-clickable": Boolean(clickable)
  }, /*#__PURE__*/React__default.createElement(AnimatePresence, {
    exitBeforeEnter: true
  }, /*#__PURE__*/React__default.createElement(StepIcon, {
    index,
    isError: isError || state === 'error',
    isLoading: isLoading || state === 'loading',
    isCurrentStep,
    isCompletedStep,
    isKeepError,
    icon: icon,
    checkIcon: checkIcon !== null && checkIcon !== void 0 ? checkIcon : defaultCheckIcon
  }))), /*#__PURE__*/React__default.createElement(StepLabel, {
    label: label,
    description: description,
    isCurrentStep,
    opacity
  })), /*#__PURE__*/React__default.createElement(Connector, {
    index: index,
    isLastStep: isLastStep,
    isKeepError: isKeepError || false,
    hasLabel: !!label || !!description,
    isCompletedStep: isCompletedStep || false
  }, !uncollapsed ? /*#__PURE__*/React__default.createElement(Collapse, {
    style: {
      width: '100%',
      marginLeft: '16px'
    },
    in: isCurrentStep
  }, (isCurrentStep || isCompletedStep) && children) : /*#__PURE__*/React__default.createElement(Collapse, {
    in: true,
    style: {
      width: '100%',
      marginLeft: '16px'
    }
  }, children))));
});
Step.displayName = 'Step';

export { Step };
